import BaseSlider from '@components/base/BaseSlider';
import LayoutUtil from '@frontend/utils/LayoutUtil';
import { getRelativeBoundsTo } from '@frontend/utils/PositioningUtil';
import { AnchorNavigationEvents } from '@frontend/utils/AnchorNavigationUtil';

export default class SubNavigation {
	private readonly element: HTMLElement;

	public static domSelectors = {
		navigationWrapper: '.m-sub-navigation__wrapper',
		scrollList: '.m-sub-navigation__scroll-list',
		mainLink: '.m-sub-navigation__main-link',
		nestedNavigation: '.m-sub-navigation__nested-navigation',
		nestedLink: '.m-sub-navigation__nested-link',
		leftButton: '.m-sub-navigation__left',
		rightButton: '.m-sub-navigation__right',
		upButton: '.m-sub-navigation__up',
		contentPane: '.m-sub-navigation__content-pane',
		headerNavigation: '.m-header__navigation-wrapper',
		gallery: '[data-slides-wrapper]'
	};

	public static stateClasses = {
		isActive: 'is-active',
		isOpen: 'is-open',
		isVisible: 'is-visible',
		hasNestedLinks: 'has-nested-links',
		hasOpenNestedLinks: 'has-open-nested-links',
		hasScrolledMaxLeft: 'has-scrolled-max-left',
		hasScrolledMaxRight: 'has-scrolled-max-right'
	};

	public static attributes = {
		linkId: 'data-link-id',
		parentId: 'data-parent-id',
		contentId: 'data-content-id'
	};

	public constructor(element: HTMLElement) {
		this.element = element;
		this.init();
	}

	private nestedNavigationHideTimeout?: number;

	private nestedNavigationShowTimeout?: number;

	private init(): void {
		this.initScrollList();
		this.initResize();
		this.initLinks();
		this.updateScrollIndicators();
		this.repositionNestedNavigation();

		document.addEventListener(AnchorNavigationEvents.change, this.anchorLinkHandler.bind(this));
	}

	private initScrollList(): void {
		const scrollList: HTMLElement | null = this.element.querySelector(SubNavigation.domSelectors.scrollList);
		const leftButton: HTMLElement | null = this.element.querySelector(SubNavigation.domSelectors.leftButton);
		const rightButton: HTMLElement | null = this.element.querySelector(SubNavigation.domSelectors.rightButton);
		const offset = 80;

		if (!scrollList) return;

		scrollList.addEventListener('scroll', (): void => {
			this.updateScrollIndicators();
			this.closeEveryNestedNavigation();
			this.repositionNestedNavigation();
		});

		if (leftButton) {
			leftButton.addEventListener('click', (): void => {
				scrollList.scrollTo({
					left: scrollList.scrollLeft - (scrollList.clientWidth - offset),
					behavior: 'smooth'
				});
			});
		}

		if (rightButton) {
			rightButton.addEventListener('click', (): void => {
				scrollList.scrollTo({
					left: scrollList.scrollLeft + (scrollList.clientWidth - offset),
					behavior: 'smooth'
				});
			});
		}
	}

	private initResize(): void {
		window.addEventListener('resize', (): void => {
			this.updateScrollIndicators();
			this.repositionNestedNavigation();
		});
	}

	private initLinks(): void {
		const upLink = this.element.querySelector(SubNavigation.domSelectors.upButton);
		const mainLinks: HTMLElement[] = Array.from(
			this.element.querySelectorAll(
				`${SubNavigation.domSelectors.mainLink}:not(.${SubNavigation.stateClasses.hasNestedLinks})`
			)
		);
		const mainLinksWithNestedLinks: HTMLElement[] = Array.from(
			this.element.querySelectorAll(
				`${SubNavigation.domSelectors.mainLink}.${SubNavigation.stateClasses.hasNestedLinks}`
			)
		);
		const nestedLinks: HTMLElement[] = Array.from(this.element.querySelectorAll(SubNavigation.domSelectors.nestedLink));
		const nestedNavigations: HTMLElement[] = Array.from(
			this.element.querySelectorAll(SubNavigation.domSelectors.nestedNavigation)
		);
		const hideTimeoutDuration = 150;
		const showTimeoutDuration = 150;

		mainLinksWithNestedLinks.forEach((item): void => {
			const id = item.getAttribute(SubNavigation.attributes.linkId);

			item.addEventListener('mouseenter', (): void => {
				clearTimeout(this.nestedNavigationHideTimeout);

				this.nestedNavigationShowTimeout = setTimeout((): void => {
					this.closeEveryNestedNavigation();
					if (id) {
						this.openNestedNavigation(id);
					}
				}, showTimeoutDuration) as any;
			});

			item.addEventListener('mouseleave', (): void => {
				clearTimeout(this.nestedNavigationShowTimeout);

				this.nestedNavigationHideTimeout = setTimeout((): void => {
					if (id) {
						this.closeNestedNavigation(id);
					}
				}, hideTimeoutDuration) as any;
			});

			item.addEventListener('touchstart', (): void => {
				const isClosed = !item.classList.contains(SubNavigation.stateClasses.isOpen);

				const linkId = item.getAttribute(SubNavigation.attributes.linkId);

				if (!linkId) return;

				this.closeEveryNestedNavigation();

				if (isClosed) {
					this.openNestedNavigation(linkId);
				}
			});
		});

		nestedNavigations.forEach((item): void => {
			item.addEventListener('mouseenter', (): void => {
				clearTimeout(this.nestedNavigationHideTimeout);
			});

			item.addEventListener('mouseleave', (): void => {
				this.nestedNavigationHideTimeout = setTimeout((): void => {
					this.closeEveryNestedNavigation();
				}, hideTimeoutDuration) as any;
			});
		});

		[...mainLinks, ...nestedLinks].forEach((item): void => {
			item.addEventListener('click', (event): void => {
				event.preventDefault();
				const id = item.getAttribute(SubNavigation.attributes.linkId);
				if (!id) return;
				this.changeContent(id);
			});
		});

		if (upLink) {
			upLink.addEventListener('click', (): void => {
				this.alignNavigation();
			});
		}

		const elementsWithLink = this.element.querySelectorAll('[data-link-id]');
		for (let i = 0; elementsWithLink[i]; i++) {
			if (elementsWithLink[i] && elementsWithLink[i].hasAttribute('data-link-id')) {
				const id = elementsWithLink[i].getAttribute('data-link-id');
				const eLink = this.element.querySelector(`[${SubNavigation.attributes.linkId}=${id}]`);
				const eContent = this.element.querySelector(`[${SubNavigation.attributes.contentId}=${id}]`);
				if (id && eLink && eContent) {
					this.changeContent(id, { alignWithViewport: false });
					console.log(id);
					break;
				}
			}
		}
		/*

		*/
	}

	private anchorLinkHandler(event: CustomEventInit): void {
		const targetElement = this.element.querySelector(event.detail.target);

		if (targetElement) {
			const contentPane = targetElement.closest('.m-sub-navigation__content-pane');

			if (contentPane) {
				this.changeContent(contentPane.id, { alignWithViewport: false });
			}
		}
	}

	private resetMainLinks(): void {
		const mainLinks: HTMLElement[] = Array.from(this.element.querySelectorAll(SubNavigation.domSelectors.mainLink));
		mainLinks.forEach((item): void => item.classList.remove(SubNavigation.stateClasses.isActive));
	}

	private resetNestedLinks(): void {
		const nestedLinks: HTMLElement[] = Array.from(this.element.querySelectorAll(SubNavigation.domSelectors.nestedLink));
		nestedLinks.forEach((item): void => item.classList.remove(SubNavigation.stateClasses.isActive));
	}

	private resetContent(): void {
		const content: HTMLElement[] = Array.from(this.element.querySelectorAll(SubNavigation.domSelectors.contentPane));
		content.forEach((item): void => item.classList.remove(SubNavigation.stateClasses.isVisible));
	}

	private resetAll(): void {
		this.closeEveryNestedNavigation();
		this.resetMainLinks();
		this.resetNestedLinks();
		this.resetContent();
	}

	private closeEveryNestedNavigation(): void {
		const nestedNavigations: HTMLElement[] = Array.from(
			this.element.querySelectorAll(SubNavigation.domSelectors.nestedNavigation)
		);
		const mainLinks: HTMLElement[] = Array.from(this.element.querySelectorAll(SubNavigation.domSelectors.mainLink));

		mainLinks.forEach((item): void => item.classList.remove(SubNavigation.stateClasses.isOpen));
		nestedNavigations.forEach((item): void => item.classList.remove(SubNavigation.stateClasses.isVisible));
		this.element.classList.remove(SubNavigation.stateClasses.hasOpenNestedLinks);
	}

	private openNestedNavigation(id: string): void {
		const mainLink = this.element.querySelector(`[${SubNavigation.attributes.linkId}=${id}]`);
		const nestedNavigation = this.element.querySelector(`[${SubNavigation.attributes.parentId}=${id}]`);

		if (!nestedNavigation || !mainLink) return;

		this.element.classList.add(SubNavigation.stateClasses.hasOpenNestedLinks);
		mainLink.classList.add(SubNavigation.stateClasses.isOpen);
		nestedNavigation.classList.add(SubNavigation.stateClasses.isVisible);
	}

	private closeNestedNavigation(id: string): void {
		const mainLink = this.element.querySelector(`[${SubNavigation.attributes.linkId}=${id}]`);
		const nestedNavigation = this.element.querySelector(`[${SubNavigation.attributes.parentId}=${id}]`);

		if (!nestedNavigation || !mainLink) return;

		this.element.classList.remove(SubNavigation.stateClasses.hasOpenNestedLinks);
		mainLink.classList.remove(SubNavigation.stateClasses.isOpen);
		nestedNavigation.classList.remove(SubNavigation.stateClasses.isVisible);
	}

	protected updateScrollIndicators(): void {
		const scrollList: HTMLElement | null = this.element.querySelector(SubNavigation.domSelectors.scrollList);

		if (!scrollList) return;

		const scrollLeft: number = Math.abs(scrollList.scrollLeft);
		const maxScrollLeft: number = scrollList.scrollWidth - scrollList.offsetWidth;

		if (scrollLeft - 1 > 0) {
			this.element.classList.add(SubNavigation.stateClasses.hasScrolledMaxLeft);
		} else {
			this.element.classList.remove(SubNavigation.stateClasses.hasScrolledMaxLeft);
		}

		if (scrollLeft + 1 < maxScrollLeft) {
			this.element.classList.add(SubNavigation.stateClasses.hasScrolledMaxRight);
		} else {
			this.element.classList.remove(SubNavigation.stateClasses.hasScrolledMaxRight);
		}
	}

	private changeContent(id: string, options = { alignWithViewport: true }): void {
		const link = this.element.querySelector(`[${SubNavigation.attributes.linkId}=${id}]`);
		const content = this.element.querySelector(`[${SubNavigation.attributes.contentId}=${id}]`);

		if (!link || !content) return;

		const parent = link.closest(SubNavigation.domSelectors.nestedNavigation);

		this.resetAll();

		link.classList.add(SubNavigation.stateClasses.isActive);
		content.classList.add(SubNavigation.stateClasses.isVisible);

		if (parent) {
			const parentId = parent.getAttribute(SubNavigation.attributes.parentId);

			const parentLink = this.element.querySelector(
				`${SubNavigation.domSelectors.mainLink}[${SubNavigation.attributes.linkId}=${parentId}]`
			);

			if (parentLink) {
				parentLink.classList.add(SubNavigation.stateClasses.isActive);
			}
		}

		const query = `${SubNavigation.domSelectors.contentPane}.${SubNavigation.stateClasses.isVisible}`;
		const visiblePane: HTMLElement | null = this.element.querySelector(query);

		if (visiblePane) {
			BaseSlider.resizeSliders(visiblePane);
		}

		if (options.alignWithViewport) {
			this.alignNavigation();
		}
	}

	private alignNavigation(): void {
		const navigation: HTMLElement | null = this.element.querySelector(SubNavigation.domSelectors.navigationWrapper);

		if (navigation) {
			LayoutUtil.scrollElementToTopAndHideNavigation(navigation, 30);
		}
	}

	protected repositionNestedNavigation(): void {
		const navigationWrapper = this.element.querySelector(SubNavigation.domSelectors.navigationWrapper);
		const navigations: HTMLElement[] = Array.from(
			this.element.querySelectorAll(SubNavigation.domSelectors.nestedNavigation)
		);
		const offset = 5;

		navigations.forEach((item): void => {
			const element: HTMLElement = item;
			const parentId = element.getAttribute(SubNavigation.attributes.parentId);
			const parent = this.element.querySelector(`[${SubNavigation.attributes.linkId}="${parentId}"]`);

			if (!navigationWrapper || !parent) return;

			const parentBounds = getRelativeBoundsTo(parent, navigationWrapper);

			element.style.top = `${parentBounds.top + parentBounds.height + offset}px `;

			if (this.isRtl()) {
				element.style.left = `${parentBounds.left + parentBounds.width - element.getBoundingClientRect().width}px`;
			} else {
				element.style.left = `${parentBounds.left}px`;
			}
		});
	}

	protected isRtl(): boolean {
		return document.dir === 'rtl';
	}
}
